import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  LinearProgress,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Download as DownloadIcon,
  Warning as WarningIcon,
  InsertDriveFile as FileIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import api, { getApiBaseUrl } from '../../utils/api';
import { getAuthAccessToken } from '../../utils/demoPreviewStorage';
import { fetchDocumentPreviewContent } from '../../utils/documentPreviewContent';

function documentAssetUrl(docId, pathSegment) {
  const base = String(getApiBaseUrl() || '').replace(/\/+$/, '');
  return `${base}/documents/${docId}/${pathSegment}`;
}

export default function DocumentViewPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(true);
  const [previewContent, setPreviewContent] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/documents/${id}`);
        if (!cancelled) setDoc(r.data.data);
      } catch {
        toast.error('Failed to load document');
        navigate('/documents');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, navigate]);

  useEffect(() => {
    if (!doc) return;
    let cancelled = false;
    (async () => {
      setPreviewLoading(true);
      setPreviewContent(null);
      try {
        const content = await fetchDocumentPreviewContent(doc, { documentAssetUrl, getAuthAccessToken });
        if (!cancelled) setPreviewContent(content);
      } catch (e) {
        console.error(e);
        if (!cancelled) setPreviewContent({ type: 'error' });
      } finally {
        if (!cancelled) setPreviewLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [doc]);

  useEffect(() => {
    return () => {
      if (previewContent?.isBlob && previewContent?.url) {
        URL.revokeObjectURL(previewContent.url);
      }
    };
  }, [previewContent]);

  const handleDownload = async () => {
    if (!doc) return;
    try {
      const token = getAuthAccessToken();
      const response = await fetch(documentAssetUrl(doc.id, 'download'), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        toast.error('Failed to download document');
        return;
      }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.file_name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error(error);
      toast.error('Failed to download');
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 120px)', minHeight: 400 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <Button startIcon={<BackIcon />} onClick={() => navigate('/documents')}>
          Back to documents
        </Button>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          {doc?.title || 'Document'}
        </Typography>
        {doc ? (
          <Tooltip title="Download">
            <IconButton onClick={handleDownload} color="primary">
              <DownloadIcon />
            </IconButton>
          </Tooltip>
        ) : null}
      </Box>

      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden', bgcolor: 'background.paper' }}>
        {previewLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
            <LinearProgress sx={{ width: '50%' }} />
          </Box>
        ) : previewContent?.type === 'text' ? (
          <Box sx={{ p: 3, overflow: 'auto', flex: 1, bgcolor: 'background.default' }}>
            <pre
              style={{
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                fontFamily: 'monospace',
                margin: 0,
                fontSize: '14px',
              }}
            >
              {previewContent.content}
            </pre>
          </Box>
        ) : previewContent?.type === 'html' ? (
          <Box sx={{ flex: 1, overflow: 'auto' }}>
            <iframe
              srcDoc={previewContent.content}
              title={doc?.title}
              style={{ width: '100%', height: '100%', minHeight: 480, border: 'none', background: '#fff' }}
              sandbox="allow-same-origin"
            />
          </Box>
        ) : previewContent?.type === 'image' ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1, p: 2 }}>
            <img
              src={previewContent.url}
              alt={doc?.title}
              style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
            />
          </Box>
        ) : previewContent?.type === 'pdf' ? (
          <Box sx={{ flex: 1, minHeight: 400 }}>
            <iframe
              src={previewContent.url}
              title={doc?.title}
              style={{ width: '100%', height: '100%', minHeight: 480, border: 'none' }}
            />
          </Box>
        ) : previewContent?.type === 'unsupported' ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
            <FileIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              Preview not available for this file type
            </Typography>
            <Button variant="contained" startIcon={<DownloadIcon />} onClick={handleDownload} sx={{ mt: 2 }}>
              Download to View
            </Button>
          </Box>
        ) : previewContent?.type === 'error' ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
            <WarningIcon sx={{ fontSize: 80, color: 'error.main', mb: 2 }} />
            <Typography variant="h6" color="error">
              Failed to load preview
            </Typography>
            <Button variant="outlined" startIcon={<DownloadIcon />} onClick={handleDownload} sx={{ mt: 2 }}>
              Try downloading instead
            </Button>
          </Box>
        ) : null}
      </Box>
    </Box>
  );
}
